/**
 * Group consecutive messages from the same sender within a time window
 * Used to reduce visual clutter by only showing avatar/name on the first message in a group
 */

import { Message } from '../types/chat';

export interface MessageGroup {
  senderId: string;
  senderName: string;
  senderAvatar?: string;
  isMine: boolean;
  messages: Message[];
}

const GROUP_TIME_WINDOW_MS = 2 * 60 * 1000; // 2 minutes

/**
 * Group messages by sender and time proximity
 */
export function groupMessages(messages: Message[]): MessageGroup[] {
  if (messages.length === 0) return [];

  const groups: MessageGroup[] = [];
  let currentGroup: MessageGroup | null = null;

  for (const message of messages) {
    const shouldStartNewGroup =
      !currentGroup ||
      currentGroup.senderId !== message.senderId ||
      isOutsideTimeWindow(
        currentGroup.messages[currentGroup.messages.length - 1].createdAt,
        message.createdAt
      );

    if (shouldStartNewGroup) {
      // Start new group
      currentGroup = {
        senderId: message.senderId,
        senderName: message.senderName,
        senderAvatar: message.senderAvatar,
        isMine: message.isMine,
        messages: [message],
      };
      groups.push(currentGroup);
    } else {
      // Add to current group
      currentGroup!.messages.push(message);
    }
  }

  return groups;
}

/**
 * Check if two timestamps are outside the grouping time window
 */
function isOutsideTimeWindow(timestamp1: string, timestamp2: string): boolean {
  const date1 = new Date(timestamp1);
  const date2 = new Date(timestamp2);
  const diffMs = Math.abs(date2.getTime() - date1.getTime());
  return diffMs > GROUP_TIME_WINDOW_MS;
}

/**
 * Check if a message is the first in its group (for UI rendering)
 */
export function isFirstInGroup(messages: Message[], index: number): boolean {
  if (index === 0) return true;

  const current = messages[index];
  const previous = messages[index - 1];

  return (
    current.senderId !== previous.senderId ||
    isOutsideTimeWindow(previous.createdAt, current.createdAt)
  );
}

/**
 * Check if a message is the last in its group (for UI rendering)
 */
export function isLastInGroup(messages: Message[], index: number): boolean {
  if (index === messages.length - 1) return true;

  const current = messages[index];
  const next = messages[index + 1];

  return (
    current.senderId !== next.senderId ||
    isOutsideTimeWindow(current.createdAt, next.createdAt)
  );
}

